import api from "../../api";

export default {
  namespaced: true,
  state: {
    // Tickets
    tickets: [],
    totalTickets: 0,
    currentPage: 1,
    pageSize: 20,
    currentTicket: null,
    currentTicketId: null, // Track which ticket is being viewed for notifications
    ticketMessages: [],

    // Departments
    departments: [],
    departmentMembers: [],

    // Topics
    topics: [],

    // User Permissions
    permissions: null,

    // Filters
    activeFilter: {
      status: null,
      userType: null,
      departmentId: null,
      topicId: null,
      assignedToMe: false,
      unassigned: false,
    },

    // Loading states
    loading: false,
    loadingMessages: false,
  },

  getters: {
    getTickets: (state) => state.tickets,
    getTotalTickets: (state) => state.totalTickets,
    getCurrentTicket: (state) => state.currentTicket,
    getTicketMessages: (state) => state.ticketMessages,
    getDepartments: (state) => state.departments,
    getDepartmentMembers: (state) => state.departmentMembers,
    getTopics: (state) => state.topics,
    getPermissions: (state) => state.permissions,
    isLoading: (state) => state.loading,
    isLoadingMessages: (state) => state.loadingMessages,
    getActiveFilter: (state) => state.activeFilter,
  },

  mutations: {
    SET_TICKETS(state, { data, totalCount, page, pageSize }) {
      state.tickets = data || [];
      state.totalTickets = totalCount || 0;
      state.currentPage = page || 1;
      state.pageSize = pageSize || 20;
    },
    SET_CURRENT_TICKET(state, ticket) {
      state.currentTicket = ticket;
    },
    SET_TICKET_MESSAGES(state, messages) {
      state.ticketMessages = messages || [];
    },
    ADD_MESSAGE(state, message) {
      state.ticketMessages.push(message);
    },
    SET_DEPARTMENTS(state, departments) {
      state.departments = departments || [];
    },
    SET_DEPARTMENT_MEMBERS(state, members) {
      state.departmentMembers = members || [];
    },
    SET_TOPICS(state, topics) {
      state.topics = topics || [];
    },
    SET_PERMISSIONS(state, permissions) {
      state.permissions = permissions;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_LOADING_MESSAGES(state, loading) {
      state.loadingMessages = loading;
    },
    SET_FILTER(state, filter) {
      state.activeFilter = { ...state.activeFilter, ...filter };
    },
    RESET_FILTER(state) {
      state.activeFilter = {
        status: null,
        userType: null,
        departmentId: null,
        topicId: null,
        assignedToMe: false,
        unassigned: false,
      };
    },
    UPDATE_TICKET_STATUS(state, { ticketId, status }) {
      const ticket = state.tickets.find(t => t.id === ticketId);
      if (ticket) ticket.status = status;
      if (state.currentTicket?.id === ticketId) {
        state.currentTicket.status = status;
      }
    },
    UPDATE_TICKET_ASSIGNMENT(state, { ticketId, assignedToUserId }) {
      const ticket = state.tickets.find(t => t.id === ticketId);
      if (ticket) ticket.assignedToUserId = assignedToUserId;
      if (state.currentTicket?.id === ticketId) {
        state.currentTicket.assignedToUserId = assignedToUserId;
      }
    },
    // Incremental updates for notifications
    UPDATE_TICKET_IN_LIST(state, updatedTicket) {
      const index = state.tickets.findIndex(t => t.id === updatedTicket.id);
      if (index !== -1) {
        // Preserve reactivity with Vue.set equivalent
        state.tickets.splice(index, 1, { ...state.tickets[index], ...updatedTicket });
      }
    },
    INCREMENT_UNREAD_COUNT(state, ticketId) {
      const ticket = state.tickets.find(t => t.id === ticketId);
      if (ticket) {
        ticket.unreadCount = (ticket.unreadCount || 0) + 1;
      }
    },
    SET_CURRENT_TICKET_ID(state, ticketId) {
      state.currentTicketId = ticketId;
    },
    APPEND_MESSAGE(state, message) {
      // Only append if not already exists
      if (!state.ticketMessages.find(m => m.id === message.id)) {
        state.ticketMessages.push(message);
      }
    },
  },

  actions: {
    // ====== TICKETS ======
    async fetchTickets({ commit, state }) {
      commit("SET_LOADING", true);
      try {
        const filter = {
          ...state.activeFilter,
          page: state.currentPage,
          pageSize: state.pageSize,
        };
        const response = await api().post("chat/v2/tickets", filter);
        if (response.data.status === 200) {
          commit("SET_TICKETS", response.data);
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching tickets:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    // Silent version for notification-triggered refreshes (no loading state)
    async fetchTicketsSilent({ commit, state }) {
      try {
        const filter = {
          ...state.activeFilter,
          page: state.currentPage,
          pageSize: state.pageSize,
        };
        const response = await api().post("chat/v2/tickets", filter);
        if (response.data.status === 200) {
          commit("SET_TICKETS", response.data);
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching tickets silently:", error);
      }
    },

    async fetchTicketDetails({ commit }, ticketId) {
      commit("SET_LOADING_MESSAGES", true);
      try {
        const response = await api().post(`chat/v2/tickets/${ticketId}`);
        if (response.data.status === 200) {
          commit("SET_CURRENT_TICKET", response.data.ticket);
          commit("SET_TICKET_MESSAGES", response.data.messages);
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching ticket details:", error);
        throw error;
      } finally {
        commit("SET_LOADING_MESSAGES", false);
      }
    },

    // Silent version for notification-triggered refreshes (no loading state)
    async fetchTicketDetailsSilent({ commit }, ticketId) {
      try {
        const response = await api().post(`chat/v2/tickets/${ticketId}`);
        if (response.data.status === 200) {
          commit("SET_CURRENT_TICKET", response.data.ticket);
          commit("SET_TICKET_MESSAGES", response.data.messages);
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching ticket details silently:", error);
      }
    },

    async sendMessage({ commit, state }, { ticketId, message }) {
      try {
        const response = await api().post(`chat/v2/tickets/${ticketId}/messages`, { message });
        if (response.data.status === 200) {
          // Add message to local state
          commit("ADD_MESSAGE", {
            id: response.data.messageId,
            ticketId,
            message,
            creationDate: new Date().toISOString(),
            seen: false,
            isSender: true,
            isSupport: true,
          });
        }
        return response.data;
      } catch (error) {
        console.error("Error sending message:", error);
        throw error;
      }
    },

    async sendImage({ commit }, { ticketId, formData }) {
      try {
        const response = await api().post(`chat/v2/tickets/${ticketId}/images`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return response.data;
      } catch (error) {
        console.error("Error sending image:", error);
        throw error;
      }
    },

    async markAsSeen({ commit }, ticketId) {
      try {
        const response = await api().post(`chat/v2/tickets/${ticketId}/mark-seen`);
        return response.data;
      } catch (error) {
        console.error("Error marking as seen:", error);
        throw error;
      }
    },

    async requestCloseTicket({ commit }, ticketId) {
      try {
        const response = await api().post(`chat/v2/tickets/${ticketId}/request-close`);
        if (response.data.status === 200) {
          commit("UPDATE_TICKET_STATUS", { ticketId, status: "pending_close" });
        }
        return response.data;
      } catch (error) {
        console.error("Error requesting close:", error);
        throw error;
      }
    },

    async closeTicket({ commit }, ticketId) {
      try {
        const response = await api().post(`chat/v2/tickets/${ticketId}/close`);
        if (response.data.status === 200) {
          commit("UPDATE_TICKET_STATUS", { ticketId, status: "closed" });
        }
        return response.data;
      } catch (error) {
        console.error("Error closing ticket:", error);
        throw error;
      }
    },

    // ====== ASSIGNMENTS ======
    async assignTicket({ commit }, { ticketId, agentUserId }) {
      try {
        const response = await api().post(`chat/v2/tickets/${ticketId}/assign`, { agentUserId });
        if (response.data.status === 200) {
          commit("UPDATE_TICKET_ASSIGNMENT", { ticketId, assignedToUserId: agentUserId });
        }
        return response.data;
      } catch (error) {
        console.error("Error assigning ticket:", error);
        throw error;
      }
    },

    async unassignTicket({ commit }, { ticketId, agentUserId }) {
      try {
        const response = await api().post(`chat/v2/tickets/${ticketId}/unassign`, { agentUserId });
        if (response.data.status === 200) {
          commit("UPDATE_TICKET_ASSIGNMENT", { ticketId, assignedToUserId: null });
        }
        return response.data;
      } catch (error) {
        console.error("Error unassigning ticket:", error);
        throw error;
      }
    },

    async transferTicket({ commit, dispatch }, { ticketId, departmentId }) {
      try {
        const response = await api().post(`chat/v2/tickets/${ticketId}/transfer`, { departmentId });
        if (response.data.status === 200) {
          dispatch("fetchTickets");
        }
        return response.data;
      } catch (error) {
        console.error("Error transferring ticket:", error);
        throw error;
      }
    },

    async fetchMyTickets({ commit }) {
      commit("SET_LOADING", true);
      try {
        const response = await api().post("chat/v2/my-tickets");
        if (response.data.status === 200) {
          commit("SET_TICKETS", { data: response.data.data, totalCount: response.data.data?.length || 0 });
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching my tickets:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    // ====== DEPARTMENTS ======
    async fetchDepartments({ commit }) {
      try {
        const response = await api().post("chat/v2/departments");
        if (response.data.status === 200) {
          commit("SET_DEPARTMENTS", response.data.data);
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching departments:", error);
        throw error;
      }
    },

    async fetchDepartmentMembers({ commit }, departmentId) {
      try {
        const response = await api().post(`chat/v2/departments/${departmentId}/members`);
        if (response.data.status === 200) {
          commit("SET_DEPARTMENT_MEMBERS", response.data.data);
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching department members:", error);
        throw error;
      }
    },

    async fetchUnassignedTickets({ commit }, departmentId) {
      commit("SET_LOADING", true);
      try {
        const response = await api().post(`chat/v2/departments/${departmentId}/unassigned`);
        if (response.data.status === 200) {
          commit("SET_TICKETS", { data: response.data.data, totalCount: response.data.data?.length || 0 });
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching unassigned tickets:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    // ====== TOPICS ======
    async fetchTopics({ commit }) {
      try {
        const response = await api().post("chat/v2/topics");
        if (response.data.status === 200) {
          commit("SET_TOPICS", response.data.topics);
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching topics:", error);
        throw error;
      }
    },

    // ====== PERMISSIONS ======
    async fetchMyPermissions({ commit }) {
      try {
        const response = await api().post("chat/v2/my-permissions");
        if (response.data.status === 200) {
          commit("SET_PERMISSIONS", response.data.data);
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching permissions:", error);
        throw error;
      }
    },

    // ====== FILTERS ======
    setFilter({ commit, dispatch }, filter) {
      commit("SET_FILTER", filter);
      dispatch("fetchTickets");
    },

    resetFilter({ commit, dispatch }) {
      commit("RESET_FILTER");
      dispatch("fetchTickets");
    },
  },
};
